import { type FC, useState } from 'react';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { FiClock, FiZap, FiCheckCircle } from 'react-icons/fi';
import type { Task } from '../types';
import TaskColumn from './TaskColumn';
import TaskCard from './TaskCard';

interface KanbanBoardProps {
  tasks: Task[];
  onUpdateStatus: (taskId: string, newStatus: 'pending' | 'in-progress' | 'completed') => void;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onArchive?: (taskId: string) => void;
}

const KanbanBoard: FC<KanbanBoardProps> = ({ tasks, onUpdateStatus, onEdit, onDelete, onArchive }) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  
  // Configure sensors with mobile touch support
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10, // 10px movement to activate drag
    },
  });
  
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250, // 250ms long press to activate
      tolerance: 5, // 5px tolerance for slight movement
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  const pendingTasks = tasks.filter((t) => t.status === 'pending');
  const inProgressTasks = tasks.filter((t) => t.status === 'in-progress');
  const completedTasks = tasks.filter((t) => t.status === 'completed');

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const taskId = active.id as string;
      const newStatus = over.id as 'pending' | 'in-progress' | 'completed';
      onUpdateStatus(taskId, newStatus);
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Find the active task for the DragOverlay
  const activeTask = activeId ? tasks.find(t => t.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 overflow-x-auto pb-4">
        <TaskColumn
          status="pending"
          title={
            <span className="flex items-center gap-2 text-sm sm:text-base">
              <FiClock className="text-gray-600 dark:text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              Pending
            </span>
          }
          tasks={pendingTasks}
          onDrop={onUpdateStatus}
          onEdit={onEdit}
          onDelete={onDelete}
          onArchive={onArchive}
        />
        <TaskColumn
          status="in-progress"
          title={
            <span className="flex items-center gap-2 text-sm sm:text-base">
              <FiZap className="text-theme dark:text-theme w-4 h-4 sm:w-5 sm:h-5" />
              In Progress
            </span>
          }
          tasks={inProgressTasks}
          onDrop={onUpdateStatus}
          onEdit={onEdit}
          onDelete={onDelete}
          onArchive={onArchive}
        />
        <TaskColumn
          status="completed"
          title={
            <span className="flex items-center gap-2 text-sm sm:text-base">
              <FiCheckCircle className="text-green-600 dark:text-green-400 w-4 h-4 sm:w-5 sm:h-5" />
              Completed
            </span>
          }
          tasks={completedTasks}
          onDrop={onUpdateStatus}
          onEdit={onEdit}
          onDelete={onDelete}
          onArchive={onArchive}
        />
      </div>
      <DragOverlay>
        {activeTask ? (
          <div className="cursor-grabbing opacity-80 rotate-3">
            <TaskCard
              task={activeTask}
              onEdit={onEdit}
              onDelete={onDelete}
              onArchive={onArchive}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default KanbanBoard;
